import React, { useState, useRef, useEffect } from 'react';
import { FiSearch, FiChevronDown, FiCheck, FiX } from 'react-icons/fi';

const SearchableSelect = ({
  options = [],
  value = '',
  onChange,
  placeholder = 'Select option...',
  searchPlaceholder = 'Search...',
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Alphabetically sorted options
  const sortedOptions = [...options].sort((a, b) => {
    const nameA = (typeof a === 'string' ? a : (a.label || a.name || '')).toLowerCase();
    const nameB = (typeof b === 'string' ? b : (b.label || b.name || '')).toLowerCase();
    return nameA.localeCompare(nameB);
  });

  // Filtered by search term
  const filteredOptions = sortedOptions.filter(opt => {
    const name = (typeof opt === 'string' ? opt : (opt.label || opt.name || '')).toLowerCase();
    return name.includes(searchTerm.toLowerCase());
  });

  const getOptionLabel = (opt) => {
    if (!opt) return '';
    return typeof opt === 'string' ? opt : (opt.label || opt.name || '');
  };

  const getOptionValue = (opt) => {
    if (!opt) return '';
    return typeof opt === 'string' ? opt : (opt.value || opt.name || '');
  };

  const selectedLabel = options.find(opt => getOptionValue(opt) === value);

  return (
    <div className="relative w-full text-left" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) {
            setIsOpen(!isOpen);
            setSearchTerm('');
          }
        }}
        className={`w-full bg-slate-800 border border-slate-700 rounded-xl p-3.5 text-xs text-white flex items-center justify-between outline-none transition-all ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-purple-500 focus:border-purple-500'
        }`}
      >
        <span className={value ? 'font-semibold text-white truncate' : 'text-gray-400 truncate'}>
          {value ? getOptionLabel(selectedLabel) || value : placeholder}
        </span>

        <div className="flex items-center gap-1.5 text-gray-400 shrink-0">
          {value && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                onChange('');
                setSearchTerm('');
              }}
              className="p-1 hover:text-red-400 transition-colors cursor-pointer"
              title="Clear selection"
            >
              <FiX className="w-3.5 h-3.5" />
            </span>
          )}
          <FiChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180 text-purple-400' : ''}`} />
        </div>
      </button>

      {/* Floating Searchable Menu */}
      {isOpen && (
        <div className="absolute z-50 left-0 right-0 mt-1.5 bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-64">
          {/* Integrated Search Input */}
          <div className="p-2.5 border-b border-slate-800 bg-slate-950/80 sticky top-0 z-10">
            <div className="relative">
              <input
                type="text"
                autoFocus
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full bg-slate-800 border border-slate-700 rounded-xl py-2 pl-8 pr-3 text-xs text-white outline-none focus:border-purple-500"
              />
              <FiSearch className="absolute left-2.5 top-2.5 text-gray-400 w-3.5 h-3.5" />
            </div>
          </div>

          {/* Options List */}
          <div className="overflow-y-auto p-1.5 space-y-1 scroll-smooth">
            {filteredOptions.length === 0 ? (
              <div className="p-4 text-center text-xs text-gray-500 font-medium">
                No matching results found
              </div>
            ) : (
              filteredOptions.map((opt, idx) => {
                const optVal = getOptionValue(opt);
                const optLbl = getOptionLabel(opt);
                const isSelected = optVal === value;

                return (
                  <div
                    key={idx}
                    onClick={() => {
                      onChange(optVal);
                      setIsOpen(false);
                      setSearchTerm('');
                    }}
                    className={`px-3 py-2 rounded-xl text-xs font-semibold cursor-pointer transition-all flex items-center justify-between ${
                      isSelected
                        ? 'bg-purple-600/30 text-purple-300 border border-purple-500/40'
                        : 'text-gray-300 hover:bg-slate-800 hover:text-white'
                    }`}
                  >
                    <span className="truncate">{optLbl}</span>
                    {isSelected && <FiCheck className="w-3.5 h-3.5 text-purple-400 shrink-0" />}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
